import React, {ReactElement} from "react";
import {throttle} from "throttle-debounce";

import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import LeadForm, {FormData} from "../../lead-forms/lead-form/LeadForm";
import SimpleFormWrapper from "../../forms/simple-form-wrapper/SimpleFormWrapper";
import MultiForm from "../../forms/multi-form/MultiForm";
import Image, {ImageObject} from "../../image/Image";
import GDPRContext from "/src/contexts/GDPRContext";
import Video, {VideoType} from "../../videos/video/Video";
import MarkdownText from "../../markdown/markdown-text/MarkdownText";
import ModuleBody from "../../layouts/module-body/ModuleBody";

import {MaybeType, MultiFormProps, SimpleFormProps} from "/src/types";
import {css, generateKeyId} from "/src/utils/utils";

import * as styles from "./landing-page-hero.module.scss";

interface LandingPageHeroProps {
  bgImage: MaybeType<ImageObject>;
  bgVideo: MaybeType<string>;
  copy: MaybeType<string>;
  ctas: MaybeType<Array<CTA>>;
  darkOverlay: MaybeType<boolean>;
  eyebrow: MaybeType<string>;
  eyebrowIcon: MaybeType<ImageObject>;
  form?: MultiFormProps | SimpleFormProps;
  headline: MaybeType<string>;
  leadFormButtonText: MaybeType<string>;
  leadFormCampaignId: MaybeType<string>;
  leadFormConfirmationRoute: MaybeType<string>;
  leadFormHeadline: MaybeType<string>;
  leadFormLastInquiryObject: MaybeType<string>;
  onLeadFormSubmitError?: (err: Error) => unknown;
  onLeadFormSubmitSuccess?: (data: FormData) => unknown;
  pardotFormHandlerUrl: MaybeType<string>;
  subtitle: MaybeType<string>;
  titleWithLineBreaks: MaybeType<string>;
  useTitleWithLineBreaks: MaybeType<boolean>;
  wistiaBgVideo?: string;
}

const LandingPageHero = (props: LandingPageHeroProps): ReactElement => {
  const {
    bgImage,
    bgVideo,
    copy,
    ctas,
    darkOverlay,
    eyebrow,
    eyebrowIcon,
    form,
    headline,
    leadFormButtonText,
    leadFormCampaignId,
    leadFormConfirmationRoute,
    leadFormHeadline,
    leadFormLastInquiryObject,
    onLeadFormSubmitError,
    onLeadFormSubmitSuccess,
    pardotFormHandlerUrl,
    subtitle,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    wistiaBgVideo,
  } = props;

  let finalCtas = ctas;
  if (
    (eyebrow === "AIR FREIGHT" || eyebrow === "OCEAN FREIGHT") &&
    process.env.ENABLE_IPB_LANDING_PAGE === "true"
  ) {
    finalCtas = [
      {
        label: "Get Rates",
        url: "/route-search/",
      },
    ];
  }

  const isDarkOverlay = darkOverlay === true;

  const throttleRef = React.useRef<throttle<() => void> | null>(null);
  const wrapperElemRef = React.useRef<null | HTMLDivElement>(null);
  const formElemRef = React.useRef<null | HTMLDivElement>(null);
  const mainElemRef = React.useRef<null | HTMLDivElement>(null);
  const {isEU} = React.useContext(GDPRContext);

  let leadForm = (
    <LeadForm
      buttonText={leadFormButtonText!}
      campaignId={leadFormCampaignId!}
      classes={styles.landingPageHero__leadform}
      confirmationRoute={leadFormConfirmationRoute}
      headline={leadFormHeadline!}
      lastInquiryObject={leadFormLastInquiryObject!}
      onMount={onResize}
      onSubmitError={onLeadFormSubmitError}
      onSubmitSuccess={onLeadFormSubmitSuccess}
      pardotFormHandlerUrl={pardotFormHandlerUrl}
    />
  );

  // if there is form, and type === simple form + layout full || type multiform
  // we will use latest form component to render lead form
  /* eslint-disable no-underscore-dangle */
  if (form != null) {
    if (form.__typename === "ContentfulSimpleForm" && form.layout === "full") {
      leadForm = (
        <SimpleFormWrapper
          classes={styles.landingPageHero__form__form}
          contentClasses={styles.landingPageHero__form__form__content}
          onMount={onResize}
          {...form}
        />
      );
    } else if (form.__typename === "ContentfulMultiStepForm") {
      leadForm = <MultiForm {...form} />;
    }
  }

  function onResize() {
    if (wrapperElemRef.current && formElemRef.current) {
      const isDesktop = window.innerWidth > 1024;

      const formHeight = formElemRef.current.offsetHeight;
      const mainHeight = mainElemRef.current?.offsetHeight ?? 0;

      const largest = Math.max(formHeight, mainHeight);
      const smallest = Math.min(formHeight, mainHeight);
      // add extra padding to make it look better
      wrapperElemRef.current.style.minHeight = isDesktop
        ? `${largest - smallest + 64}px`
        : "auto";
    }
  }

  /* this is to make sure that form doesn't overflow to the next content */
  React.useEffect(() => {
    throttleRef.current = throttle(300, onResize);

    setTimeout(() => {
      onResize();
    });

    window.addEventListener("resize", throttleRef.current);

    return () => {
      if (throttleRef.current) {
        window.removeEventListener("resize", throttleRef.current);
      }
    };
  });

  // resize on form happens before extra content is rendered via isEU
  // this causes the form to overflow to the next section
  React.useEffect(() => {
    onResize();
  }, [isEU]);

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.landingPageHero__videoBg)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoTitle={headline!}
            type={VideoType.autoplay}
            videoSource={wistiaBgVideo}
            showControl={false}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.landingPageHero__videoBg)}>
        <Video
          videoTitle={headline!}
          type={VideoType.autoplay}
          videoSource={bgVideo!}
          videoClasses={styles.landingPageHero__videoBg__video}
          showControl={false}
        />
      </div>
    );
  };

  const hasBg = bgVideo || wistiaBgVideo || bgImage;

  return (
    <div className={css(styles.landingPageHero)}>
      <div
        ref={wrapperElemRef}
        className={css(
          styles.landingPageHero__contentWrapper,
          !hasBg ? styles.landingPageHero__contentWrapperNoBg : ""
        )}
      >
        <div className={styles.landingPageHero__bgWrapper}>
          {!bgVideo && !wistiaBgVideo && bgImage && (
            <Image
              image={bgImage}
              objectFit="cover"
              classes={styles.landingPageHero__bgImg}
            />
          )}

          {constructVideoComponent()}

          {isDarkOverlay && (
            <div className={styles.landingPageHero__overlay}></div>
          )}
        </div>
        <ModuleBody classes={styles.landingPageHero__body}>
          <div className={styles.landingPageHero__header}>
            <div>
              {eyebrow && (
                <div className={styles.landingPageHero__header__eyebrowWrapper}>
                  {eyebrowIcon && (
                    <Image
                      image={eyebrowIcon}
                      classes={styles.landingPageHero__header__eyebrowIcon}
                    />
                  )}
                  <p
                    className={css(
                      "eyebrow",
                      styles.landingPageHero__header__eyebrow
                    )}
                  >
                    {eyebrow}
                  </p>
                </div>
              )}

              {useTitleWithLineBreaks && titleWithLineBreaks != null && (
                <h1
                  className={css(
                    "h1",
                    styles.landingPageHero__header__headline
                  )}
                >
                  <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html:
                        titleWithLineBreaks != null
                          ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                          : "",
                    }}
                  />
                </h1>
              )}

              {!useTitleWithLineBreaks && headline != null && (
                <h1
                  className={css(
                    "h1",
                    styles.landingPageHero__header__headline
                  )}
                >
                  {headline}
                </h1>
              )}

              {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
              {finalCtas && (
                <ul className={styles.landingPageHero__header__ctas}>
                  {(process.env.ENABLE_IPB_LANDING_PAGE === "true"
                    ? finalCtas
                    : finalCtas.filter(cta => cta.label !== "Get Rates")
                  ).map((cta, i) => {
                    return (
                      <li
                        key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                        className={styles.landingPageHero__header__cta}
                      >
                        <CTAButton
                          darkMode
                          cta={cta}
                          kind={i === 0 ? "primary" : "secondary"}
                          classes={
                            cta.label === "Get Rates"
                              ? styles.getRatesButton
                              : ""
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className={css(styles.landingPageHero__formReserved)} />

              <div
                ref={formElemRef}
                className={css(styles.landingPageHero__form)}
              >
                {leadForm}
              </div>
            </div>
          </div>
        </ModuleBody>
      </div>
      <ModuleBody ref={mainElemRef} classes={styles.landingPageHero__main}>
        <div className={styles.landingPageHero__mainWrapper}>
          {subtitle != null && (
            <h2 className={css("h2", styles.landingPageHero__main__subtitle)}>
              {subtitle}
            </h2>
          )}

          {copy != null && (
            <MarkdownText
              text={copy}
              classes={styles.landingPageHero__main__copy}
            />
          )}

          <div className={css(styles.landingPageHero__formReserved)} />
        </div>
      </ModuleBody>

      {leadForm != null && (
        <div className={css(styles.landingPageHero__formMobile)}>
          {leadForm}
        </div>
      )}
    </div>
  );
};

export default LandingPageHero;
